import { cva, type VariantProps } from 'class-variance-authority';

/**
 * The one field surface. `Input`, `Textarea`, `SelectTrigger`, `DatePicker`,
 * `MultiSelect`, `ServerSelect` and `ServerMultiSelect` all wear it, so a field
 * never looks *almost* like the field beside it.
 *
 * It lives in its own folder rather than in `ui/Input/` because three of its
 * consumers are not inputs, and a selection control reaching into `ui/Input/`
 * for a *trigger* skin is exactly the coupling U22 exists to prevent.
 *
 * Named `*_CLASSES` deliberately: the class law (U19) inspects `cva()` literals
 * and `*_CLASSES` initialisers and nothing else. As the bare identifier
 * `FIELD_BASE` this string was invisible to it — the kit's most-reused skin sat
 * outside the U7/W3 law meant to govern it.
 */
export const FIELD_BASE_CLASSES =
  'flex w-full rounded-md border border-input bg-card text-body text-foreground transition-colors duration-fast ease-standard placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive';

/** The ONLY place a field height is written. */
export const FIELD_SIZE_CLASSES = {
  sm: 'h-8 px-md text-caption',
  md: 'h-10 px-md',
  lg: 'h-12 px-lg',
};

export const FIELD_SHAPE_CLASSES = {
  /** A native input: it lays out its own text. */
  input: '',
  /** A button that opens a panel: label at the inline start, chevron at the end. */
  trigger: 'items-center justify-between gap-sm text-start [&>span]:truncate',
  /**
   * A multi-line surface. Use `fieldBlockVariants` instead of this shape on a
   * control whose height is its content — the size map below still writes an
   * `h-*`, which is inert on a `<textarea>` only until something needs `rows`
   * to mean anything (see the note on `fieldBlockVariants`).
   */
  block: 'items-start',
  /**
   * A container holding SEVERAL controls that must read as one field — a number
   * beside its unit, a number beside its `%`.
   *
   * Two things it must do that no other shape does. The focus ring is
   * `focus-within` rather than `focus-visible`, because a `<div>` never receives
   * focus itself and the base's ring would simply never fire; and the disabled
   * treatment is driven by `:has()` off whatever it wraps, for the same reason.
   * Its children wear `FIELD_SLOT_CLASSES`, which take their own border, padding
   * and ring back off — two rings around one control is worse than none.
   */
  island:
    'items-stretch gap-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-muted has-[:disabled]:opacity-60',
};

/**
 * A control that sits INSIDE a `shape: 'island'` field: it gives back the
 * border, the background, the padding, the width and the focus ring, because the
 * island already draws all five for the group.
 *
 * No size map, deliberately. The island writes the text scale and a font size
 * inherits, so a slot literally cannot disagree with the field it sits in — the
 * failure mode a second size map would reintroduce.
 */
export const FIELD_SLOT_CLASSES =
  'min-w-0 flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:bg-transparent';

/** The same, for a slot that should size to its own content rather than fill. */
export const FIELD_SLOT_AUTO_CLASSES =
  'w-auto min-w-0 shrink-0 flex-none border-0 bg-transparent p-0 shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:bg-transparent';

/**
 * A square adornment the height of its field — a colour swatch, a stepper.
 *
 * Here rather than in the component that needed it first, because "as tall as
 * the field" is a fact about the field surface: `ColorInput` hardcoded `h-10
 * w-12` and was therefore the one control in the kit that could not be rendered
 * at `sm` (P10.2's done-when forbids exactly that).
 */
export const FIELD_SQUARE_SIZE_CLASSES = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

/**
 * The block sizes: the SAME scale as `FIELD_SIZE_CLASSES` with the height taken
 * out and the block padding put in its place.
 *
 * Both maps exist because the two shapes disagree about what a size *is*. A
 * single-line field's size is a height — one line of text, vertically centred in
 * a fixed box. A multi-line field's size is a text size plus its padding, and the
 * height is however many lines it holds. Writing both from one map is what
 * produced the defect this replaced: `Textarea` composed `shape: 'block'` at the
 * default `md`, inherited `h-10`, and patched it with `min-h-24` — so `rows` was
 * inert, the field could not grow, and a pixel dimension was hardcoded in a
 * component (the exact thing the P10.2 done-when forbids).
 */
export const FIELD_BLOCK_SIZE_CLASSES = {
  sm: 'px-md py-xs text-caption',
  md: 'px-md py-sm',
  lg: 'px-lg py-md',
};

/**
 * `isInvalid` is for a trigger that CANNOT carry `aria-invalid`.
 *
 * ARIA gives a plain `<button>` no `aria-invalid` — it has no value to be
 * invalid — so a popover trigger has no attribute for the base's
 * `aria-[invalid=true]:` selector to hang on and needs this variant instead.
 * Everything that DOES support the attribute leaves it alone, so the ring can
 * never disagree with what assistive tech reads. Never both on one element.
 */
export const fieldVariants = cva(FIELD_BASE_CLASSES, {
  variants: {
    fieldSize: FIELD_SIZE_CLASSES,
    shape: FIELD_SHAPE_CLASSES,
    isInvalid: { true: 'border-destructive focus-visible:ring-destructive', false: '' },
  },
  defaultVariants: { fieldSize: 'md', shape: 'input', isInvalid: false },
});

/**
 * The same surface for a control whose height is its CONTENT — a textarea, a
 * code editor, a rows list. It writes no `h-*`, `min-h-*` or `max-h-*` at all:
 * the caller decides the height with `rows`, or with the number of rows it
 * renders, and a control that wanted a floor would be saying its content is not
 * what sizes it.
 *
 * `isInvalid` is deliberately absent for the same reason it exists on
 * `fieldVariants`: every consumer of this variant is a real form control that
 * carries `aria-invalid`, so the base's `aria-[invalid=true]:` selector already
 * has something to hang on and a second switch could only disagree with it.
 */
export const fieldBlockVariants = cva([FIELD_BASE_CLASSES, 'items-start'], {
  variants: {
    fieldSize: FIELD_BLOCK_SIZE_CLASSES,
  },
  defaultVariants: { fieldSize: 'md' },
});

/** A square adornment sized from the field scale — see `FIELD_SQUARE_SIZE_CLASSES`. */
export const fieldSquareVariants = cva('shrink-0 rounded-md border border-input bg-card', {
  variants: { fieldSize: FIELD_SQUARE_SIZE_CLASSES },
  defaultVariants: { fieldSize: 'md' },
});

export type FieldVariants = VariantProps<typeof fieldVariants>;
export type FieldSize = NonNullable<FieldVariants['fieldSize']>;
